import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../../services/api';
import { toast } from 'react-toastify';
import { flattenQuery } from 'helpers/flattenQuery';
import { FilterConfig } from 'hooks/useListQuery';
import { array, enforce, nullable, num, numeric, object, runSchema, str, unknownValue } from 'validation';
import { filterConfigSchema } from 'api/shared/listConfig';
import { toDraft } from '../toDraft';

// Wire types re-derived from nap-backend (routes/folders.js,
// modules/generic_functions/folders/getFolders.js, queryBuilder.js,
// migration 0172_table_folders.sql) — NOT from the prior hand-written
// interface, which was assigned from `any` and drifted: zone_name is
// nullable (LEFT JOIN), created_at/updated_at are always present, and
// child_count arrives as a numeric string (a COUNT(*) subquery — Postgres
// serializes bigint as a string). Validated wire data is immutable, so the
// types are deeply readonly.
export interface Folder {
  readonly folder_id: number;
  readonly name: string;
  readonly description: string | null;
  readonly zone_id: number;
  readonly zone_name: string | null;
  readonly parent_folder_id: number | null;
  readonly parent_folder_name: string | null;
  readonly child_count: number;
  readonly created_at: string;
  readonly updated_at: string;
}

// Child folders (GET /folders/:id/children) come from a query without the
// parent LEFT JOIN, so the row carries no parent_folder_name — the children
// all share the expanded parent. Otherwise identical to a Folder row.
export type ChildFolder = Omit<Folder, 'parent_folder_name'>;

interface ChildFoldersResponse {
  readonly folders: readonly ChildFolder[];
}

// Folder audit trail (GET /folders/audit_trail/:id). The envelope wraps the
// object identity around the rows; each row exposes user_name (joined from
// users), NOT username, and omits the object_type/object_id carried on the
// envelope — so this is a distinct shape from the generic AuditTrailRecord.
export interface FolderAuditTrailRecord {
  readonly audit_trail_id: number;
  readonly audit_type: string;
  readonly user_id: number | null;
  readonly user_name: string | null;
  readonly audit_details: unknown;
  readonly created_at: string;
}

interface FolderAuditTrailResponse {
  readonly object_type: string;
  readonly object_id: number;
  readonly audit_trail: readonly FolderAuditTrailRecord[];
}

export interface HeaderConfigEntry {
  readonly header_key: string;
  readonly label: string;
  readonly header_type: string;
}

interface FolderListPagination {
  readonly total_records: number;
  readonly total_pages: number;
  readonly current_page: number;
  readonly limit: number;
  readonly offset: number;
}

interface FolderListResponse {
  readonly folders: readonly Folder[];
  readonly pagination: FolderListPagination;
  readonly config: readonly HeaderConfigEntry[];
  readonly filtersConfig: readonly FilterConfig[];
}

// Exported so the single-folder loader (`api/folders/endpoints/getFolder`) validates the
// `GET /folders/:folder_id` body against the same schema as the list rows.
export const folderSchema = object<Folder>({
  folder_id: num(),
  name: str(),
  description: nullable(str()),
  zone_id: num(),
  zone_name: nullable(str()),
  parent_folder_id: nullable(num()),
  parent_folder_name: nullable(str()),
  child_count: numeric(),
  created_at: str(),
  updated_at: str(),
});

const headerConfigSchema = object<HeaderConfigEntry>({
  header_key: str(),
  label: str(),
  header_type: str(),
});

const folderListPaginationSchema = object<FolderListPagination>({
  total_records: num(),
  total_pages: num(),
  current_page: num(),
  limit: num(),
  offset: num(),
});

const folderListResponseSchema = object<FolderListResponse>({
  folders: array(folderSchema),
  pagination: folderListPaginationSchema,
  config: array(headerConfigSchema),
  filtersConfig: array(filterConfigSchema),
});

const childFolderSchema = object<ChildFolder>({
  folder_id: num(),
  name: str(),
  description: nullable(str()),
  zone_id: num(),
  zone_name: nullable(str()),
  parent_folder_id: nullable(num()),
  child_count: numeric(),
  created_at: str(),
  updated_at: str(),
});

const childFoldersResponseSchema = object<ChildFoldersResponse>({
  folders: array(childFolderSchema),
});

const folderAuditTrailRecordSchema = object<FolderAuditTrailRecord>({
  audit_trail_id: num(),
  audit_type: str(),
  user_id: nullable(num()),
  user_name: nullable(str()),
  audit_details: unknownValue(),
  created_at: str(),
});

const folderAuditTrailResponseSchema = object<FolderAuditTrailResponse>({
  object_type: str(),
  object_id: numeric(),
  audit_trail: array(folderAuditTrailRecordSchema),
});

interface PaginationState {
  readonly totalItems: number;
  readonly totalPages: number;
  readonly currentPage: number;
  readonly pageSize: number;
}

interface FoldersState {
  readonly folders: readonly Folder[];
  readonly pagination: PaginationState;
  readonly status: string | null;
  readonly error: string | null;
  readonly auditTrail: FolderAuditTrailResponse | null;
  readonly config: readonly HeaderConfigEntry[];
  readonly filtersConfig: readonly FilterConfig[];
  readonly loading: {
    readonly getFolders: boolean;
    readonly getFoldersPaginated: boolean;
    readonly createFolder: boolean;
    readonly updateFolder: boolean;
    readonly deleteFolder: boolean;
    readonly getFolderAuditTrail: boolean;
    readonly getChildFolders: boolean;
  };
  readonly childFolders: Record<number, readonly ChildFolder[]>;
}

const initialState: FoldersState = {
  folders: [],
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 2000,
  },
  status: null,
  error: null,
  auditTrail: null,
  config: [],
  filtersConfig: [],
  loading: {
    getFolders: false,
    getFoldersPaginated: false,
    createFolder: false,
    updateFolder: false,
    deleteFolder: false,
    getFolderAuditTrail: false,
    getChildFolders: false,
  },
  childFolders: {},
};

// Fetch Folders (non-paginated, for dropdowns — all folders in flat list)
export const getFolders = createAsyncThunk<FolderListResponse>('folders/getFolders', async () => {
  const response = await api.get('/folders', {
    params: {
      limit: initialState.pagination.pageSize,
      root_only: false,
    },
  });
  const result = runSchema(folderListResponseSchema, response.data, 'getFolders');
  if (!result.ok) {
    throw new Error('folders response failed validation');
  }
  return enforce(result.value, result.warnings, ['folders[*].folder_id', 'folders[*].parent_folder_id', 'pagination.total_records', 'pagination.total_pages']);
});

// Fetch Folders (paginated, for list page — root-level only; use getChildFolders on expand)
export const getFoldersPaginated = createAsyncThunk<FolderListResponse, Record<string, any>>('folders/getFoldersPaginated', async (params) => {
  const queryParams = {
    limit: initialState.pagination.pageSize,
    root_only: true,
    ...params,
  };
  const response = await api.get('/folders', {
    params: flattenQuery(queryParams),
  });
  const result = runSchema(folderListResponseSchema, response.data, 'getFoldersPaginated');
  if (!result.ok) {
    throw new Error('folders response failed validation');
  }
  return enforce(result.value, result.warnings, ['folders[*].folder_id', 'folders[*].parent_folder_id', 'pagination.total_records', 'pagination.total_pages']);
});

// Create Folder
export const createFolder = createAsyncThunk<
  Folder,
  {
    name: string;
    description?: string;
    zone_id?: number;
    zone_name?: string;
    parent_folder_id?: number | null;
  },
  { rejectValue: { message: string; error?: string } }
>('folders/createFolder', async (folderData, { rejectWithValue }) => {
  try {
    const response = await api.post('/folders', folderData);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: err.message });
  }
});

// Update Folder
export const updateFolder = createAsyncThunk<
  Folder,
  {
    folder_id: number;
    name?: string;
    description?: string;
    zone_id?: number;
    zone_name?: string;
    parent_folder_id?: number | null;
  },
  { rejectValue: { message: string; error?: string } }
>('folders/updateFolder', async (folderData, { rejectWithValue }) => {
  try {
    const response = await api.put(`/folders/${folderData.folder_id}`, folderData);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: err.message });
  }
});

// Delete Folder
export const deleteFolder = createAsyncThunk<{ message: string }, number, { rejectValue: { message: string; error?: string } }>(
  'folders/deleteFolder',
  async (folderId, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/folders/${folderId}`);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

// Get Folder Audit Trail
export const getFolderAuditTrail = createAsyncThunk<FolderAuditTrailResponse, number>('folders/getFolderAuditTrail', async (folderId) => {
  const response = await api.get(`/folders/audit_trail/${folderId}`);
  const result = runSchema(folderAuditTrailResponseSchema, response.data, 'getFolderAuditTrail');
  if (!result.ok) {
    throw new Error('folder audit trail response failed validation');
  }
  return enforce(result.value, result.warnings, ['audit_trail[*].audit_trail_id']);
});

// Get Child Folders
export const getChildFolders = createAsyncThunk<{ parentId: number; folders: readonly ChildFolder[] }, number>('folders/getChildFolders', async (parentId) => {
  const response = await api.get(`/folders/${parentId}/children`);
  const result = runSchema(childFoldersResponseSchema, response.data, 'getChildFolders');
  if (!result.ok) {
    throw new Error('child folders response failed validation');
  }
  const { folders } = enforce(result.value, result.warnings, ['folders[*].folder_id', 'folders[*].parent_folder_id']);
  return { parentId, folders };
});

const foldersSlice = createSlice({
  name: 'folders',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ================== getFolders ==================
      .addCase(getFolders.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.getFolders = true;
      })
      .addCase(getFolders.fulfilled, (state, action) => {
        state.folders = toDraft(action.payload.folders);
        state.status = 'succeeded';
        state.loading.getFolders = false;
      })
      .addCase(getFolders.rejected, (state, action) => {
        state.status = 'failed';
        state.loading.getFolders = false;
        state.error = action.error.message || 'Failed to fetch folders';
      });

    builder
      .addCase(getFoldersPaginated.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.getFoldersPaginated = true;
      })
      .addCase(getFoldersPaginated.fulfilled, (state, action) => {
        state.status = 'succeeded';

        const { folders, config, filtersConfig, pagination } = action.payload;

        state.folders = toDraft(folders);
        state.config = toDraft(config);
        state.filtersConfig = toDraft(filtersConfig);

        state.pagination.totalItems = pagination.total_records;
        state.pagination.totalPages = pagination.total_pages;
        state.pagination.pageSize = pagination.limit || state.pagination.pageSize;
        state.pagination.currentPage = pagination.current_page > pagination.total_pages ? 1 : pagination.current_page;

        state.loading.getFoldersPaginated = false;
      })
      .addCase(getFoldersPaginated.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch folders';
        state.loading.getFoldersPaginated = false;
      });

    builder
      // ================== createFolder ==================
      .addCase(createFolder.pending, (state) => {
        state.loading.createFolder = true;
        state.error = null;
      })
      .addCase(createFolder.fulfilled, (state, action) => {
        state.folders.push(action.payload);
        state.status = 'succeeded';
        state.loading.createFolder = false;

        const toastId = 'data-created-folder';
        if (!toast.isActive(toastId)) {
          toast.success('Folder created successfully!', { toastId });
        }
      })
      .addCase(createFolder.rejected, (state, action) => {
        state.loading.createFolder = false;
        state.status = 'failed';

        const payload = action.payload as { message?: string; error?: string } | undefined;

        state.error = payload?.error || payload?.message || 'Failed to create folder';

        const toastId = 'data-failed-to-create-folder';
        if (!toast.isActive(toastId)) {
          toast.error(`Failed to create new folder! ${state.error}`, {
            toastId,
          });
        }
      })

      // ================== updateFolder ==================
      .addCase(updateFolder.pending, (state) => {
        state.loading.updateFolder = true;
        state.error = null;
      })
      .addCase(updateFolder.fulfilled, (state, action) => {
        const index = state.folders.findIndex((folder) => folder.folder_id === action.payload.folder_id);
        if (index !== -1) {
          state.folders[index] = action.payload;
        }
        state.status = 'succeeded';
        state.loading.updateFolder = false;

        const toastId = 'data-updated-folder';
        if (!toast.isActive(toastId)) {
          toast.success('Folder updated successfully!', { toastId });
        }
      })
      .addCase(updateFolder.rejected, (state, action) => {
        state.loading.updateFolder = false;
        state.status = 'failed';

        const payload = action.payload as { message?: string; error?: string } | undefined;

        state.error = payload?.error || payload?.message || 'Failed to update folder';

        const toastId = 'data-failed-to-update-folder';
        if (!toast.isActive(toastId)) {
          toast.error(`Failed to update the folder. Message: ${state.error}`, {
            toastId,
          });
        }
      })

      // ================== deleteFolder ==================
      .addCase(deleteFolder.pending, (state) => {
        state.loading.deleteFolder = true;
        state.error = null;
      })
      .addCase(deleteFolder.fulfilled, (state, action) => {
        state.folders = state.folders.filter((folder) => folder.folder_id !== action.meta.arg);
        state.status = 'succeeded';
        state.loading.deleteFolder = false;

        const toastId = 'data-deleted-folder';
        if (!toast.isActive(toastId)) {
          toast.success('Folder deleted successfully!', { toastId });
        }
      })
      .addCase(deleteFolder.rejected, (state, action) => {
        state.loading.deleteFolder = false;
        state.status = 'failed';

        const payload = action.payload as { message?: string; error?: string } | undefined;

        state.error = payload?.error || payload?.message || 'Failed to delete folder';

        const toastId = 'data-failed-to-delete-folder';
        if (!toast.isActive(toastId)) {
          toast.error(`Failed to delete the folder! ${state.error}`, {
            toastId,
          });
        }
      })

      // ================== getFolderAuditTrail ==================
      .addCase(getFolderAuditTrail.pending, (state) => {
        state.loading.getFolderAuditTrail = true;
      })
      .addCase(getFolderAuditTrail.fulfilled, (state, action) => {
        state.loading.getFolderAuditTrail = false;
        state.status = 'succeeded';
        state.auditTrail = toDraft(action.payload);
      })
      .addCase(getFolderAuditTrail.rejected, (state, action) => {
        state.loading.getFolderAuditTrail = false;
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch folder audit trail';
      })

      // ================== getChildFolders ==================
      .addCase(getChildFolders.pending, (state) => {
        state.loading.getChildFolders = true;
      })
      .addCase(getChildFolders.fulfilled, (state, action) => {
        state.loading.getChildFolders = false;
        state.childFolders[action.payload.parentId] = toDraft(action.payload.folders);
      })
      .addCase(getChildFolders.rejected, (state, action) => {
        state.loading.getChildFolders = false;
        state.error = action.error.message || 'Failed to fetch child folders';
      });
  },
});

export default foldersSlice.reducer;
